import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowForward,
  CheckCircle,
  HourglassEmpty,
  LockOpen,
  Lock,
} from "@mui/icons-material";
import { Timestamp } from "firebase/firestore";

/* ── Status definitions ────────────────────────────── */
const STATUSES = [
  { key: "open", label: "Open", color: "info", icon: LockOpen },
  { key: "in-progress", label: "In Progress", color: "warning", icon: HourglassEmpty },
  { key: "resolved", label: "Resolved", color: "success", icon: CheckCircle },
  { key: "closed", label: "Closed", color: "default", icon: Lock },
];

const STATUS_ORDER = STATUSES.map((s) => s.key);

const getStatusMeta = (key) =>
  STATUSES.find((s) => s.key === key) || STATUSES[0];

/**
 * Returns only forward statuses (no going back).
 * If already closed, returns empty array (terminal state).
 */
const getNextStatuses = (current) => {
  const idx = STATUS_ORDER.indexOf(current);
  if (idx === -1) return STATUSES.slice(1); // unknown → allow all except open
  return STATUSES.filter((_, i) => i > idx);
};

export default function StatusChangeDialog({
  open,
  currentStatus,
  onClose,
  onConfirm,
}) {
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");

  const currentMeta = getStatusMeta(currentStatus);
  const nextOptions = getNextStatuses(currentStatus);

  const handleConfirm = () => {
    if (!selected) return;
    onConfirm({
      newStatus: selected,
      comment: comment.trim(),
      timestamp: Timestamp.now(),
    });
    // reset
    setSelected(null);
    setComment("");
  };

  const handleClose = () => {
    setSelected(null);
    setComment("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Change Status
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Current status */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 0.5, display: "block" }}
          >
            Current Status
          </Typography>
          <Chip
            icon={React.createElement(currentMeta.icon, { sx: { fontSize: 16 } })}
            label={currentMeta.label}
            color={currentMeta.color}
            sx={{ fontWeight: 600 }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Next status options */}
        {nextOptions.length > 0 ? (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ mb: 1, display: "block" }}
            >
              Move to
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {nextOptions.map((opt) => {
                const isSelected = selected === opt.key;
                return (
                  <Chip
                    key={opt.key}
                    icon={React.createElement(opt.icon, { sx: { fontSize: 16 } })}
                    label={opt.label}
                    color={opt.color}
                    variant={isSelected ? "filled" : "outlined"}
                    onClick={() => setSelected(opt.key)}
                    sx={{
                      fontWeight: 600,
                      cursor: "pointer",
                      border: isSelected
                        ? "2px solid"
                        : undefined,
                    }}
                  />
                );
              })}
            </Box>

            {/* Comment */}
            <TextField
              label="Comment (optional)"
              placeholder="Reason for status change…"
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              size="small"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </>
        ) : (
          <Typography color="text.secondary" variant="body2">
            This project has reached its final status.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        {nextOptions.length > 0 && (
          <Button
            variant="contained"
            disabled={!selected}
            onClick={handleConfirm}
            endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
            }}
          >
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
